import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import customersApi from 'api/customers'
import { fetchRecommendedEventsForCustomer } from 'api/affinity'

/**
 * Audience drill-down content, rendered inside the conversation-level right
 * inspector (CustomerInspector supplies the drawer chrome + close). Analysts
 * click row after row and this content swaps in place. Reuses the same cr_api
 * endpoints v1 uses (prod + mock).
 */
const money = (v) => {
  const currency = (localStorage.getItem('userCurrency') || 'EUR').toUpperCase()
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v || 0)
  } catch {
    return `${v || 0} ${currency}`
  }
}

export default function AudienceDrillPanel({ customerId, audienceTitle }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [details, setDetails] = useState(null)
  const [summary, setSummary] = useState(null)
  const [recs, setRecs] = useState([])

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(false)
    const run = async () => {
      try {
        const [d, s] = await Promise.all([
          customersApi.fetchCustomerDetailsAsync(customerId),
          customersApi.fetchCustomerSalesSummaryAsync(customerId),
        ])
        if (!active) return
        if (d.error || s.error) throw new Error('customer fetch failed')
        setDetails(d.data)
        setSummary(s.data)
        const r = await fetchRecommendedEventsForCustomer(customerId)
        if (!active) return
        setRecs(r.success ? r.data || [] : [])
      } catch {
        if (active) setError(true)
      } finally {
        if (active) setLoading(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [customerId])

  const salesPerEvent = summary?.sales_per_event || []
  const name = details
    ? `${details.first_name || ''} ${details.last_name || ''}`.trim() || t('Sentinel.V2.customer.fallbackName', { id: customerId })
    : t('Sentinel.V2.customer.title')
  const initials = (name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()

  return (
    <div className="fd-customer-panel">
      <div className="fd-customer-panel__content">
        {loading && <div className="fd-customer-panel__status fd-body-sm">{t('Sentinel.V2.customer.loading')}</div>}
        {error && !loading && <div className="fd-customer-panel__status fd-customer-panel__status--error fd-body-sm">{t('Sentinel.V2.customer.error')}</div>}

        {!loading && !error && details && summary && (
          <div className="fd-customer-panel__layout">
            <div className="fd-customer-panel__identity">
              <div className="fd-customer-panel__avatar" aria-hidden="true">
                {initials}
              </div>
              <div className="fd-customer-panel__identity-copy">
                <div className="fd-customer-panel__name">{name}</div>
                <div className="fd-customer-panel__id fd-mono">
                  #{details.id ?? customerId}{details.city ? ` · ${details.city}` : ''}
                </div>
              </div>
            </div>

            {audienceTitle && (
              <div className="fd-customer-panel__audience fd-body-sm">
                in audience · <span style={{ color: 'var(--text-primary)' }}>{audienceTitle}</span>
              </div>
            )}

            <div className="fd-customer-panel__metrics">
              <div className="fd-metric fd-metric--sm"><div className="fd-metric__label">{t('Sentinel.V2.customer.events')}</div><div className="fd-metric__value">{salesPerEvent.length}</div></div>
              <div className="fd-metric fd-metric--sm"><div className="fd-metric__label">{t('Sentinel.V2.customer.tickets')}</div><div className="fd-metric__value">{(summary.global_revenue?.total_tickets ?? 0).toLocaleString()}</div></div>
              <div className="fd-metric fd-metric--sm"><div className="fd-metric__label">{t('Sentinel.V2.customer.lifetime')}</div><div className="fd-metric__value">{money(summary.global_revenue?.total_purchase_value)}</div></div>
            </div>

            <div className="fd-customer-panel__contact fd-body-sm">
              {details.email && (<><span className="fd-customer-panel__label">{t('Sentinel.V2.customer.email')}</span><span className="fd-customer-panel__contact-value fd-customer-panel__email">{details.email}</span></>)}
              {(details.phone1 || details.phone2) && (<><span className="fd-customer-panel__label">{t('Sentinel.V2.customer.phone')}</span><span className="fd-customer-panel__contact-value fd-mono">{details.phone1 || details.phone2}</span></>)}
              <span className="fd-customer-panel__label">{t('Sentinel.V2.customer.ads')}</span>
              <span className="fd-customer-panel__contact-value">
                <span className={`fd-badge ${details.ads_opt_out ? 'fd-badge--danger' : 'fd-badge--success'}`}>
                  <span className="fd-badge__dot" />{details.ads_opt_out ? t('Sentinel.V2.customer.optedOut') : t('Sentinel.V2.customer.optedIn')}
                </span>
              </span>
            </div>

            <section className="fd-customer-panel__section">
              <div className="fd-customer-panel__section-header">
                <span className="fd-eyebrow">{t('Sentinel.V2.customer.recommended')}</span>
                <span className="fd-badge fd-badge--affinity">{t('Sentinel.V2.customer.viaAffinity')}</span>
              </div>
              <div className="fd-customer-panel__table-scroll">
                <table className="fd-table">
                  <thead><tr><th>{t('Sentinel.V2.customer.event')}</th><th>{t('Sentinel.V2.customer.date')}</th><th>{t('Sentinel.V2.customer.category')}</th></tr></thead>
                  <tbody>
                    {recs.length === 0 && <tr><td colSpan={3} style={{ color: 'var(--text-tertiary)' }}>{t('Sentinel.V2.customer.noRecommendations')}</td></tr>}
                    {recs.slice(0, 10).map((ev, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--text-link)' }}>{ev.title}</td>
                        <td className="fd-mono">{ev.start_date_time ? moment.utc(ev.start_date_time).format('DD.MM.YY') : '—'}</td>
                        <td>{ev.category || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="fd-customer-panel__section">
              <div className="fd-customer-panel__section-header">
                <span className="fd-eyebrow">{t('Sentinel.V2.customer.visited')}</span>
              </div>
              <div className="fd-customer-panel__table-scroll">
                <table className="fd-table">
                  <thead><tr><th>{t('Sentinel.V2.customer.date')}</th><th>{t('Sentinel.V2.customer.event')}</th><th style={{ textAlign: 'right' }}>{t('Sentinel.V2.customer.spent')}</th></tr></thead>
                  <tbody>
                    {salesPerEvent.length === 0 && <tr><td colSpan={3} style={{ color: 'var(--text-tertiary)' }}>{t('Sentinel.V2.customer.noVisits')}</td></tr>}
                    {salesPerEvent.slice(0, 12).map((ev, i) => (
                      <tr key={i}>
                        <td className="fd-mono">{ev.event_date || '—'}</td>
                        <td style={{ color: 'var(--text-primary)' }}>{ev.title}</td>
                        <td className="num">{money(ev.total_spent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

AudienceDrillPanel.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  audienceTitle: PropTypes.string,
}
